import {
  confirmSignUp as amplifyConfirmSignUp,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

/** Rethrow Cognito's exceptions with a readable message. */
async function wrap<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (err) {
    throw new Error(describeAuthError(err));
  }
}

async function hasSession(): Promise<boolean> {
  try {
    return Boolean((await fetchAuthSession()).tokens?.idToken);
  } catch {
    return false;
  }
}

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DEV_AUTH, DEV_USER_EMAIL } from "./config";
import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
  type AuthUser,
} from "./context";
import { describeAuthError } from "./errors";

/**
 * Sign-up, confirm, sign-in against the Weply pool. The confirmation code
 * arrives as a link into `/confirm`, which calls `confirmSignUp` here.
 *
 * The dev bypass short-circuits all of it: no pool, no token; `ApiProvider`
 * sends the impersonation headers instead.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(
    DEV_AUTH ? "signed_in" : "loading",
  );
  const [user, setUser] = useState<AuthUser | null>(
    DEV_AUTH ? { email: DEV_USER_EMAIL } : null,
  );

  const refresh = useCallback(async () => {
    try {
      const current = await getCurrentUser();
      let email = current.signInDetails?.loginId ?? current.username;
      try {
        const attrs = await fetchUserAttributes();
        if (attrs.email) email = attrs.email;
      } catch {
        // Attributes can be briefly unavailable; the login id is fine.
      }
      setUser({ email });
      setStatus("signed_in");
    } catch {
      setUser(null);
      setStatus("signed_out");
    }
  }, []);

  useEffect(() => {
    if (DEV_AUTH) return;
    void refresh();
    return Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn") void refresh();
      if (
        payload.event === "signedOut" ||
        payload.event === "tokenRefresh_failure"
      ) {
        setUser(null);
        setStatus("signed_out");
      }
    });
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isDevSession: DEV_AUTH,
      async signUp(email, password) {
        await wrap(() =>
          amplifySignUp({
            username: email,
            password,
            options: { userAttributes: { email } },
          }),
        );
      },
      async confirmSignUp(email, code) {
        await wrap(() =>
          amplifyConfirmSignUp({ username: email, confirmationCode: code }),
        );
      },
      async resendCode(email) {
        await wrap(() => resendSignUpCode({ username: email }));
      },
      async signIn(email, password) {
        // A stale session blocks a fresh sign-in with "already signed in".
        if (await hasSession()) await amplifySignOut();
        const result = await wrap(() =>
          amplifySignIn({ username: email, password }),
        );
        if (result.isSignedIn) await refresh();
        else if (result.nextStep.signInStep === "CONFIRM_SIGN_UP") {
          throw new Error(
            "Please confirm your email first — check your inbox for the link.",
          );
        } else {
          throw new Error(
            `This account needs a sign-in step we do not support (${result.nextStep.signInStep}).`,
          );
        }
      },
      async signOut() {
        try {
          if (!DEV_AUTH) await amplifySignOut();
        } finally {
          setUser(null);
          setStatus("signed_out");
        }
      },
    }),
    [status, user, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
